import { useState, useMemo } from "react";
import { axiosInstance } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorHandler";
import { Banknote, Printer } from "lucide-react";

const COLONES_DENOMS = [20000, 10000, 5000, 2000, 1000, 500, 100, 50, 25];
const DOLLAR_DENOMS = [100, 50, 20, 10, 5, 1];

const fmtCRC = (n) => `₡${(Number(n) || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtUSD = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * CashOutlayDialog — Calculadora de billetes/monedas para Salida de Efectivo.
 * Al confirmar, crea una transacción `type: "salida"` con el desglose
 * (denominations_colones / denominations_dollars) y abre un ticket 80mm
 * imprimible con la misma tabla + motivo + firma.
 */
export function CashOutlayDialog({ open, onOpenChange, box, cashRegister, user, onSaved }) {
  const usaDolares = Boolean(
    (cashRegister?.initial_cash_dollars || 0) > 0 ||
    (cashRegister?.total_cash_dollars || 0) > 0
  );

  const [countsCRC, setCountsCRC] = useState({});
  const [monedasSueltas, setMonedasSueltas] = useState(0);
  const [countsUSD, setCountsUSD] = useState({});
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const subtotalsCRC = useMemo(() => COLONES_DENOMS.map((d) => (Number(countsCRC[d]) || 0) * d), [countsCRC]);
  const totalCRC = useMemo(
    () => subtotalsCRC.reduce((a, b) => a + b, 0) + (Number(monedasSueltas) || 0),
    [subtotalsCRC, monedasSueltas]
  );
  const subtotalsUSD = useMemo(() => DOLLAR_DENOMS.map((d) => (Number(countsUSD[d]) || 0) * d), [countsUSD]);
  const totalUSD = useMemo(() => subtotalsUSD.reduce((a, b) => a + b, 0), [subtotalsUSD]);

  const reset = () => {
    setCountsCRC({});
    setMonedasSueltas(0);
    setCountsUSD({});
    setReason("");
  };

  const printTicket = ({ txId, createdAt }) => {
    const rows = COLONES_DENOMS.map((d, i) => `
      <tr>
        <td>${fmtCRC(d).replace(",00", "")}</td>
        <td class="c">${Number(countsCRC[d]) || 0}</td>
        <td class="r">${fmtCRC((Number(countsCRC[d]) || 0) * d)}</td>
      </tr>`).join("");
    const monedaRow = `
      <tr>
        <td>MONEDAS</td>
        <td class="c">—</td>
        <td class="r">${fmtCRC(Number(monedasSueltas) || 0)}</td>
      </tr>`;
    const usdRows = usaDolares
      ? `
        <tr class="section"><td colspan="3">DÓLARES</td></tr>
        ${DOLLAR_DENOMS.map((d) => `
          <tr>
            <td>${fmtUSD(d).replace(".00", "")}</td>
            <td class="c">${Number(countsUSD[d]) || 0}</td>
            <td class="r">${fmtUSD((Number(countsUSD[d]) || 0) * d)}</td>
          </tr>`).join("")}
        <tr class="total"><td colspan="2">TOTAL USD</td><td class="r">${fmtUSD(totalUSD)}</td></tr>`
      : "";
    const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>Salida de Efectivo ${txId.slice(0, 8)}</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  body { font-family: 'Courier New', monospace; font-size: 12px; margin: 0; color: #000; }
  h2 { text-align: center; margin: 4px 0; font-size: 14px; }
  .meta { font-size: 11px; margin: 6px 0; }
  .meta div { display: flex; justify-content: space-between; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  th, td { padding: 2px 3px; font-size: 11px; }
  thead th { border-bottom: 1px solid #000; text-align: left; }
  td.c { text-align: center; }
  td.r { text-align: right; }
  tr.section td { background: #eee; font-weight: bold; padding: 3px; text-align: center; margin-top: 4px; }
  tr.total td { border-top: 1px solid #000; font-weight: bold; padding-top: 4px; }
  .reason { margin-top: 10px; font-size: 12px; }
  .sig { margin-top: 24px; text-align: center; border-top: 1px dashed #000; padding-top: 4px; font-size: 10px; }
  .footer { text-align: center; font-size: 10px; margin-top: 10px; color: #444; }
</style></head><body>
  <h2>SALIDA DE EFECTIVO</h2>
  <div class="meta">
    <div><span>Caja:</span><span>${(box?.name || "").toString().slice(0, 24)}</span></div>
    <div><span>Fecha:</span><span>${new Date(createdAt).toLocaleString("es-CR")}</span></div>
    <div><span>Cajero:</span><span>${user?.username || ""}</span></div>
    <div><span>#Doc:</span><span>${txId.slice(0, 8).toUpperCase()}</span></div>
  </div>
  <table>
    <thead><tr><th>DENOM</th><th class="c">CANT</th><th class="r">SUBTOTAL</th></tr></thead>
    <tbody>
      <tr class="section"><td colspan="3">COLONES</td></tr>
      ${rows}
      ${monedaRow}
      <tr class="total"><td colspan="2">TOTAL CRC</td><td class="r">${fmtCRC(totalCRC)}</td></tr>
      ${usdRows}
    </tbody>
  </table>
  <div class="reason"><b>MOTIVO:</b><br>${(reason || "").replace(/</g, "&lt;")}</div>
  <div class="sig">FIRMA RESPONSABLE</div>
  <div class="footer">Documento generado por sistema</div>
  <script>window.onload = () => { window.print(); setTimeout(()=>window.close(), 400); };</script>
</body></html>`;
    const w = window.open("", "_blank", "width=380,height=640");
    if (!w) {
      toast.error("El navegador bloqueó la impresión. Habilitá popups.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const handleSave = async () => {
    if (totalCRC <= 0 && totalUSD <= 0) {
      toast.error("Ingresá al menos una cantidad");
      return;
    }
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error("Escribí un motivo (mínimo 3 caracteres)");
      return;
    }
    const desglose = COLONES_DENOMS
      .map((d) => (Number(countsCRC[d]) || 0) > 0 ? `${fmtCRC(d).replace(",00", "")}×${countsCRC[d]}` : null)
      .filter(Boolean).join(" · ");
    const description = `SALIDA EFECTIVO — ${reason.trim()}${desglose ? "\n[" + desglose + (monedasSueltas > 0 ? ` · monedas ${fmtCRC(monedasSueltas)}` : "") + "]" : ""}`;

    const denominations_colones = { ...Object.fromEntries(COLONES_DENOMS.map(d => [String(d), Number(countsCRC[d]) || 0])), monedas_sueltas: Number(monedasSueltas) || 0 };
    const denominations_dollars = usaDolares ? Object.fromEntries(DOLLAR_DENOMS.map(d => [String(d), Number(countsUSD[d]) || 0])) : null;

    setSaving(true);
    try {
      const res = await axiosInstance.post(`/transactions?box_id=${box.id}`, {
        type: "salida",
        amount_colones: totalCRC,
        amount_dollars: totalUSD,
        description,
        denominations_colones,
        denominations_dollars,
      });
      toast.success("Salida registrada");
      printTicket({ txId: res.data.id, createdAt: res.data.created_at || new Date().toISOString() });
      reset();
      onOpenChange(false);
      if (onSaved) onSaved(res.data);
    } catch (e) {
      toast.error(getErrorMessage(e, "No se pudo registrar la salida"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) { onOpenChange(v); if (!v) reset(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cash-outlay-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Banknote className="w-5 h-5 text-rose-600" /> Salida de Efectivo</DialogTitle>
          <DialogDescription>Contá los billetes y monedas que salen de caja. El sistema suma y arma el documento.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold mb-2 text-slate-700">COLONES</h4>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-100 text-slate-700">
                  <th className="text-left p-2 border">Denominación</th>
                  <th className="text-center p-2 border w-24">Cantidad</th>
                  <th className="text-right p-2 border w-40">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {COLONES_DENOMS.map((d) => (
                  <tr key={d} className="hover:bg-slate-50">
                    <td className="p-2 border font-mono">{fmtCRC(d).replace(",00", "")}</td>
                    <td className="p-1 border">
                      <Input
                        type="number" min="0" step="1"
                        value={countsCRC[d] ?? ""}
                        onChange={(e) => setCountsCRC({ ...countsCRC, [d]: e.target.value.replace(/^0+(?=\d)/, "") })}
                        className="h-8 text-center"
                        data-testid={`outlay-crc-${d}`}
                      />
                    </td>
                    <td className="p-2 border text-right font-mono">{fmtCRC((Number(countsCRC[d]) || 0) * d)}</td>
                  </tr>
                ))}
                <tr>
                  <td className="p-2 border font-mono">MONEDAS (sueltas)</td>
                  <td className="p-1 border text-center text-slate-400">—</td>
                  <td className="p-1 border">
                    <Input
                      type="number" min="0" step="1"
                      value={monedasSueltas || ""}
                      onChange={(e) => setMonedasSueltas(e.target.value.replace(/^0+(?=\d)/, ""))}
                      className="h-8 text-right"
                      placeholder="₡ total en monedas"
                      data-testid="outlay-crc-monedas"
                    />
                  </td>
                </tr>
                <tr className="bg-rose-50 font-bold">
                  <td className="p-2 border" colSpan={2}>TOTAL COLONES</td>
                  <td className="p-2 border text-right text-rose-700 text-base" data-testid="outlay-total-crc">{fmtCRC(totalCRC)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {usaDolares && (
            <div>
              <h4 className="text-sm font-semibold mb-2 text-slate-700">DÓLARES</h4>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-100 text-slate-700">
                    <th className="text-left p-2 border">Denominación</th>
                    <th className="text-center p-2 border w-24">Cantidad</th>
                    <th className="text-right p-2 border w-40">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {DOLLAR_DENOMS.map((d) => (
                    <tr key={d} className="hover:bg-slate-50">
                      <td className="p-2 border font-mono">{fmtUSD(d).replace(".00", "")}</td>
                      <td className="p-1 border">
                        <Input
                          type="number" min="0" step="1"
                          value={countsUSD[d] ?? ""}
                          onChange={(e) => setCountsUSD({ ...countsUSD, [d]: e.target.value.replace(/^0+(?=\d)/, "") })}
                          className="h-8 text-center"
                          data-testid={`outlay-usd-${d}`}
                        />
                      </td>
                      <td className="p-2 border text-right font-mono">{fmtUSD((Number(countsUSD[d]) || 0) * d)}</td>
                    </tr>
                  ))}
                  <tr className="bg-rose-50 font-bold">
                    <td className="p-2 border" colSpan={2}>TOTAL DÓLARES</td>
                    <td className="p-2 border text-right text-rose-700 text-base" data-testid="outlay-total-usd">{fmtUSD(totalUSD)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1 block">Motivo / Detalle <span className="text-rose-600">*</span></label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: pago proveedor XYZ, retiro para depósito, etc."
              rows={2}
              data-testid="outlay-reason"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button
            onClick={handleSave}
            disabled={saving || (totalCRC <= 0 && totalUSD <= 0) || !reason.trim()}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="outlay-save-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            {saving ? "Guardando..." : "Guardar e imprimir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
